import { parse, pathForCalendar } from '../routes.ts';

/**
 * The `@wtfalch/calls` room key a calendar event's call uses (agora#341).
 *
 * agora#341 left this as an open decision: a synthetic id derived from the
 * event's own id, costing a small mapping the calls session store has to
 * accept as an opaque string, or a real Matrix room created per event,
 * costing a Synapse room per meeting and tying this to `wtfalch/chat`. This
 * picks the synthetic-id route -- it needs no chat dependency, matches "not
 * a second place to go" more closely, and is reversible: nothing downstream
 * of `startOrJoinRoomCall` cares what shape a room id has, so switching to a
 * real Matrix room later only means changing this one function.
 *
 * Keyed on `uid`, not `CalendarEvent.id` (agora#341 round 3) -- in a JMAP
 * calendar each account's own copy of an invite carries its own `id`, and
 * only `uid` is stable across the organiser's and every attendee's copy
 * (`@wtfalch/calendar`'s `CalendarEvent.uid` doc comment). Keying on `id`
 * would give every invitee a different room for what is supposed to be one
 * call.
 *
 * No recurrence id: the join link lives in the series' `virtualLocations`
 * and can only name the series, so every occurrence shares one room, the
 * same one the link opens.
 */
export function eventCallRoomId(uid: string): string {
  return `event:${uid}`;
}

/** The in-app path that opens an event's call: `/h/<agoraId>/calendar/call/<uid>`
 *  (`routes.ts` owns the shape). */
export function eventCallPath(agoraId: string, uid: string): string {
  return pathForCalendar(agoraId, { date: null, eventId: null, callUid: uid });
}

/** The join URL written into an event's `virtualLocations`. */
export function eventCallUrl(origin: string, agoraId: string, uid: string): string {
  return `${origin}${eventCallPath(agoraId, uid)}`;
}

/** The agora id and uid inside an Agora event join URL, or `null` for any
 *  other link. Carries the agora id rather than dropping it (agora#392):
 *  a link copied into the wrong agora's event still parses, so a caller
 *  that only compared uids treated it as its own call regardless of which
 *  agora it actually opened -- see `Event.tsx`'s own `isOwnCallLink`. */
export function parseEventCallUrl(href: string): { agoraId: string; uid: string } | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const route = parse(url.pathname);
  if (route.kind !== 'agora' || route.section !== 'calendar') return null;
  const uid = route.location.callUid;
  return uid === undefined ? null : { agoraId: route.agoraId, uid };
}

/** Whether an event carries a call link of this agora's own -- one that
 *  names this event's uid and this agora, not a link copied from elsewhere. */
export function hasOwnEventCall(
  event: { uid: string; virtualLocations?: readonly { uri: string }[] },
  agoraId: string,
): boolean {
  return (event.virtualLocations ?? []).some((v) => {
    const parsed = parseEventCallUrl(v.uri);
    return parsed !== null && parsed.uid === event.uid && parsed.agoraId === agoraId;
  });
}

/** The call opens this long before the event starts, and stays open this long
 *  after it ends. */
export const EVENT_CALL_OPENS_BEFORE_MS = 10 * 60_000;
export const EVENT_CALL_OPEN_AFTER_MS = 60 * 60_000;

/** When an event's call can be joined, from the event's own start and end. */
export function eventCallWindow(
  startMs: number,
  endMs: number,
): { validFrom: number; expiresAt: number } {
  return {
    validFrom: startMs - EVENT_CALL_OPENS_BEFORE_MS,
    expiresAt: endMs + EVENT_CALL_OPEN_AFTER_MS,
  };
}

/** The label a guest-link entry in `virtualLocations` carries. The service
 *  returns the link's secret once, so what is needed to revoke it and end its
 *  call later is kept beside it, in the entry's own name. */
const GUEST_ENTRY = /^agora-call:v1:([A-Za-z0-9_-]{8,128}):([A-Za-z0-9_-]{8,128})$/;

export function guestEntryName(sessionId: string, linkId: string): string {
  return `agora-call:v1:${sessionId}:${linkId}`;
}

/** The session and link an event's guest-link entry names, or `null`. */
export function parseGuestEntry(
  entries: readonly { uri: string; name?: string | undefined }[] | undefined,
): { sessionId: string; linkId: string; uri: string } | null {
  for (const entry of entries ?? []) {
    const match = GUEST_ENTRY.exec(entry.name ?? '');
    if (match?.[1] && match[2]) return { sessionId: match[1], linkId: match[2], uri: entry.uri };
  }
  return null;
}
